import { useEffect, useState } from 'react';
import { settingsApi, type SettingsData, type SettingsRules } from '../lib/data.js';
import { SUPPORTED_CURRENCIES } from '../lib/format.js';
import { PageHeader, Card, NumberField, OreField, Select } from '../components/ui.js';
import { CsvImportCard } from '../components/CsvImportCard.js';

/**
 * Settings / Indstillinger (sheet "Settings"). Two cards:
 *  1. Danske regler — editable reference defaults (gældsfaktor, LTV, rådighed, FIRE-rater),
 *     persisted via PUT /api/settings which merges the `rules` key into settings.constants
 *     without touching the Net-Worth module's `networth` key. Currency + locale are read-only.
 *     These are reference numbers only; the calculators use their own built-in constants.
 *  2. Importér — the CsvImportCard component (components/CsvImportCard.tsx); parsing lives
 *     in lib/csvImport.ts.
 */

function Labeled({ label, children }: { label: string; children: React.ReactNode }): JSX.Element {
  return (
    <label className="block">
      <span className="mb-1 block text-hv-xs uppercase tracking-wide text-hv-text-tertiary">{label}</span>
      {children}
    </label>
  );
}

export function Settings(): JSX.Element {
  const [data, setData] = useState<SettingsData | null>(null);
  const reload = () => settingsApi.load().then(setData);
  useEffect(() => {
    void reload();
  }, []);

  if (!data) return <PageHeader title="Settings" subtitle="Loading…" />;

  const rules = data.rules;
  const patch = (p: Partial<SettingsRules>) => settingsApi.saveRules({ ...rules, ...p }).then(setData);

  // Persist the new display currency, then reload so every page re-renders in it (simplest
  // reliable way to propagate the module-level currency in format.ts across all routes).
  const changeCurrency = (currency: string): void => {
    if (currency === data.currency) return;
    void settingsApi.saveCurrency(currency, rules).then(() => window.location.reload());
  };

  return (
    <div className="max-w-3xl">
      <PageHeader
        title="Settings"
        subtitle={
          <>
            Reference values for Danish rules and import of entries. Reproduces the spreadsheet's{' '}
            <span className="font-mono">Settings</span> tab.
          </>
        }
      />

      {/* Display currency (selectable) + derived locale */}
      <Card className="mb-6 p-4">
        <div className="mb-3 text-hv-sm font-medium text-hv-text">General settings</div>
        <div className="grid grid-cols-2 gap-4">
          <Labeled label="Currency">
            <Select<string>
              value={data.currency}
              options={SUPPORTED_CURRENCIES.map((c) => ({ value: c, label: c }))}
              onChange={changeCurrency}
            />
          </Labeled>
          <Labeled label="Language / format">
            <div className="rounded-hv-md border border-hv-border bg-hv-surface-alt px-2.5 py-1.5 text-hv-sm text-hv-text-secondary">
              {data.locale}
            </div>
          </Labeled>
        </div>
        <p className="mt-3 text-hv-xs text-hv-text-tertiary">
          Changing the currency reloads the app so every page formats amounts in it. Stored amounts are unchanged.
        </p>
      </Card>

      {/* Danish rules */}
      <Card className="mb-6 p-4">
        <div className="mb-3 text-hv-sm font-medium text-hv-text">Danish rules (reference values)</div>
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
          <Labeled label="Debt-to-income (gældsfaktor) — soft cap">
            <NumberField value={rules.debtFactorSoft} step={0.1} onCommit={(v) => patch({ debtFactorSoft: v })} suffix="×" />
          </Labeled>
          <Labeled label="Debt-to-income (gældsfaktor) — hard cap">
            <NumberField value={rules.debtFactorHard} step={0.1} onCommit={(v) => patch({ debtFactorHard: v })} suffix="×" />
          </Labeled>
          <Labeled label="LTV (belåningsgrad) — owner-occupied">
            <NumberField value={rules.ltvHome * 100} step={1} onCommit={(v) => patch({ ltvHome: v / 100 })} suffix="%" />
          </Labeled>
          <Labeled label="LTV (belåningsgrad) — holiday home">
            <NumberField value={rules.ltvHoliday * 100} step={1} onCommit={(v) => patch({ ltvHoliday: v / 100 })} suffix="%" />
          </Labeled>
          <Labeled label="LTV (belåningsgrad) — commercial">
            <NumberField value={rules.ltvCommercial * 100} step={1} onCommit={(v) => patch({ ltvCommercial: v / 100 })} suffix="%" />
          </Labeled>
          <Labeled label="Living cost per adult / mo.">
            <OreField value={rules.livingCostAdultOre} onCommit={(v) => patch({ livingCostAdultOre: v })} />
          </Labeled>
          <Labeled label="Living cost per child / mo.">
            <OreField value={rules.livingCostChildOre} onCommit={(v) => patch({ livingCostChildOre: v })} />
          </Labeled>
          <Labeled label="Default withdrawal rate">
            <NumberField value={rules.withdrawalRate * 100} step={0.1} onCommit={(v) => patch({ withdrawalRate: v / 100 })} suffix="%" />
          </Labeled>
          <Labeled label="Default inflation">
            <NumberField value={rules.inflation * 100} step={0.1} onCommit={(v) => patch({ inflation: v / 100 })} suffix="%" />
          </Labeled>
        </div>
        <p className="mt-3 text-hv-xs text-hv-text-tertiary">
          These are reference values and are saved automatically. The calculators still use their own built-in constants.
        </p>
      </Card>

      {/* CSV import */}
      <CsvImportCard />
    </div>
  );
}
